import { useCallback } from 'react';
import type { CarouselApi } from '@/app/_components/ui/carousel';
import type { AutoplayType } from 'embla-carousel-autoplay';

/**
 * Hook to control autoplay plugin from carousel controls (arrows, dots)
 * Provides handlers for hover, focus, and click interactions
 */
export function useCarouselAutoplay(api: CarouselApi | undefined) {
  // Explicit `| undefined`: embla's module augmentation types plugins().autoplay
  // as always present, but it only exists on carousels that actually register
  // the plugin — the honest return type puts the single needed guard at the
  // call sites instead of scattering `?.` everywhere.
  const getAutoplay = useCallback((): AutoplayType | undefined => {
    return api?.plugins().autoplay;
  }, [api]);

  const stopAutoplay = useCallback(() => {
    getAutoplay()?.stop();
  }, [getAutoplay]);

  const playAutoplay = useCallback(() => {
    getAutoplay()?.play();
  }, [getAutoplay]);

  const resetAutoplay = useCallback(() => {
    getAutoplay()?.reset();
  }, [getAutoplay]);

  // For use on interactive elements (buttons, dots)
  const autoplayHandlers = {
    onMouseEnter: stopAutoplay,
    onMouseLeave: playAutoplay,
  };

  // For use on containers with focusable children
  const autoplayContainerHandlers = {
    onMouseEnter: stopAutoplay,
    onMouseLeave: playAutoplay,
    onFocus: stopAutoplay,
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      // Only restart if focus leaves the container entirely
      // relatedTarget is null when focus leaves browser window
      if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
        playAutoplay();
      }
    },
  };

  return {
    stopAutoplay,
    playAutoplay,
    resetAutoplay,
    autoplayHandlers,
    autoplayContainerHandlers,
  };
}
